import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Box,
  Typography,
  Chip
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import moment from "moment";
import { useApi } from "../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../shared/app-configs/sc-app-config";
import useNotification from "../../../../../shared/hooks/useNotification";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";

interface ScPdfUploaderModalProps {
  open: boolean;
  onClose: () => void;
  scId: string;
  scNo: string;
  scVersionNo: string;
  scNoPdfUrl: string;
  scVersionNoPdfUrl: string;
  submissionStatus: string;
  scUploadedFiles: any[];
  cbLicensingCode: string;
  onUploadSuccess: (responseData?: any) => void;
  scLastUpdatedDate?: string;
  scCreationDate?: string;
}

export const ScPdfUploaderModal: React.FC<ScPdfUploaderModalProps> = ({
  open,
  onClose,
  scId,
  scNo,
  scVersionNo,
  scNoPdfUrl,
  scVersionNoPdfUrl,
  submissionStatus,
  scUploadedFiles,
  cbLicensingCode,
  onUploadSuccess,
  scLastUpdatedDate,
  scCreationDate,
}) => {
  const { executePost } = useApi();
  const { notify } = useNotification();
  const [selectedFileA, setSelectedFileA] = useState<File | null>(null);
  const [selectedFileB, setSelectedFileB] = useState<File | null>(null);
  const [errorA, setErrorA] = useState<string | null>(null);
  const [errorB, setErrorB] = useState<string | null>(null);
  const [successA, setSuccessA] = useState<boolean>(false);
  const [successB, setSuccessB] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isBaseAvailable = !!scNoPdfUrl;
  const isVersionAvailable = !!scVersionNoPdfUrl;
  const hasNoAmendments = scNo === scVersionNo;
  const isBothAvailable = isBaseAvailable && isVersionAvailable;
  const isUploadDisabled = isBothAvailable || (isBaseAvailable && hasNoAmendments);

  // Normalize CB code and SC version for the example template names
  const normalizedCb = cbLicensingCode.toLowerCase().replace('cb-', 'cb-');
  const expectedCb = normalizedCb.startsWith('cb-') ? normalizedCb : `cb-${normalizedCb}`;
  
  // Use UTC to match backend _normalize_date_string which splits on 'T' (UTC date portion)
  const dateStr = scLastUpdatedDate ? moment.utc(scLastUpdatedDate).format('YYYYMMDD') : '';

  const templateNameA = `sc_${expectedCb}_${scNo.toLowerCase()}_${dateStr}.pdf`;
  
  let displayScVersionNo = scVersionNo || scNo;
  if (displayScVersionNo.toLowerCase().endsWith("-00")) {
    displayScVersionNo = displayScVersionNo.substring(0, displayScVersionNo.length - 3) + "-01";
  } else if (!displayScVersionNo.includes("-")) {
    displayScVersionNo = displayScVersionNo + "-01";
  }
  const templateNameB = `sc_${expectedCb}_${displayScVersionNo.toLowerCase()}_${dateStr}.pdf`;

  const getVersionSuffix = (ver: string) => {
    if (!ver) return "-01+";
    const parts = ver.split("-");
    if (parts.length > 0) {
      const lastPart = parts[parts.length - 1];
      if (/^\d+$/.test(lastPart)) {
        return `-${lastPart}`;
      }
    }
    return "-01+";
  };
  const versionSuffix = getVersionSuffix(displayScVersionNo);

  const handleClose = () => {
    if (uploading) return;
    setSelectedFileA(null);
    setSelectedFileB(null);
    setErrorA(null);
    setErrorB(null);
    setSuccessA(false);
    setSuccessB(false);
    onClose();
  };

  const copyTemplateA = () => {
    navigator.clipboard.writeText(templateNameA);
    notify('Base Certificate template name copied!', 'success');
  };
  const copyTemplateB = () => {
    navigator.clipboard.writeText(templateNameB);
    notify('Versioned Certificate template name copied!', 'success');
  };

  const validateFileName = (file: File, targetSlot?: 'A' | 'B'): { valid: boolean; slot: 'A' | 'B'; error: string | null } => {
    const filename = file.name;

    // 1. Intercept redundant base file drops if already uploaded/available
    const extractedMiddlePart = filename.match(/^sc_([a-zA-Z0-9\-]+)_([a-zA-Z0-9\-.]+)_(\d{8})\.[pP][dD][fF]$/)?.[2]?.toLowerCase() || '';
    if (extractedMiddlePart.endsWith("-00") || extractedMiddlePart === scNo.toLowerCase()) {
      if (isBaseAvailable) {
        return {
          valid: false,
          slot: 'A',
          error: "Base certificate is already uploaded. You only need to upload versioned amendment certificates."
        };
      }
    }

    // 2. History Duplicate Check (Files already saved in the database)
    const uploadedFiles = scUploadedFiles || [];
    const duplicateInDb = uploadedFiles.some((f: any) => f.fileName === filename);
    if (duplicateInDb) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `File already exists: '${filename}' has already been uploaded for this Scope Certificate.`
      };
    }

    // 3. Cross-Slot Session Check (File selected in both Slot A and Slot B simultaneously)
    if (targetSlot === 'A' && selectedFileB && selectedFileB.name === filename) {
      return {
        valid: false,
        slot: 'A',
        error: "File already selected: This file is already chosen for the Amended Certificate slot."
      };
    }
    if (targetSlot === 'B' && selectedFileA && selectedFileA.name === filename) {
      return {
        valid: false,
        slot: 'B',
        error: "File already selected: This file is already chosen for the Original Certificate slot."
      };
    }

    const pattern = /^sc_([a-zA-Z0-9\-]+)_([a-zA-Z0-9\-.]+)_(\d{8})\.[pP][dD][fF]$/;
    const match = filename.match(pattern);
    
    if (!match) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: "Invalid filename format. Expected structure: sc_[cb-code]_[scNo/scVersionNo]_[date_of_issue].pdf"
      };
    }
    
    const [_, extractedCb, extractedScVer, extractedDateStr] = match;
    
    // 1. Check Certification Body
    if (extractedCb.toLowerCase() !== expectedCb) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Certification Body mismatch. Filename contains '${extractedCb}' but expected '${expectedCb}'.`
      };
    }
    
    // 2. Check Date Validity and Match against scLastUpdatedDate
    const year = parseInt(extractedDateStr.substring(0, 4));
    const month = parseInt(extractedDateStr.substring(4, 6));
    const day = parseInt(extractedDateStr.substring(6, 8));
    
    if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Invalid date '${extractedDateStr}' in filename. Please provide a valid calendar date in YYYYMMDD format.`
      };
    }
    
    const dateObj = new Date(year, month - 1, day);
    if (dateObj.getFullYear() !== year || dateObj.getMonth() !== month - 1 || dateObj.getDate() !== day) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Invalid date '${extractedDateStr}' in filename. Please provide a valid calendar date.`
      };
    }

    if (!scLastUpdatedDate) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: "Scope Certificate is missing its last updated date (scLastUpdatedDate) in the database. Please contact support."
      };
    }

    // Use UTC to match backend which extracts UTC date portion from ISO timestamps
    const expectedDateStr = moment.utc(scLastUpdatedDate).format('YYYYMMDD');
    if (extractedDateStr !== expectedDateStr) {
      return {
        valid: false,
        slot: targetSlot || 'A',
        error: `Date mismatch: the filename uses date '${extractedDateStr}', but the certificate's last updated date is '${expectedDateStr}'. Please rename your file to use the date shown in the Suggested File Name above.`
      };
    }

    // 3. Classify/Map based on SC version segment
    const cleanExtractedScVer = extractedScVer.toLowerCase();
    const cleanScNo = scNo.toLowerCase();

    if (targetSlot) {
      if (targetSlot === 'A') {
        if (cleanExtractedScVer !== cleanScNo) {
          return {
            valid: false,
            slot: 'A',
            error: `Base Certificate mismatch. Filename contains '${extractedScVer}' but expected base scNo '${scNo}'.`
          };
        }
        return { valid: true, slot: 'A', error: null };
      } else {
        const cleanDisplayScVersionNo = displayScVersionNo.toLowerCase();
        if (cleanExtractedScVer !== cleanDisplayScVersionNo) {
          return {
            valid: false,
            slot: 'B',
            error: `Versioned Certificate mismatch. Filename contains '${extractedScVer}' but expected '${displayScVersionNo}'.`
          };
        }
        return { valid: true, slot: 'B', error: null };
      }
    }

    if (cleanExtractedScVer.endsWith("-00") || cleanExtractedScVer === cleanScNo) {
      if (cleanExtractedScVer !== cleanScNo) {
        return {
          valid: false,
          slot: 'A',
          error: `Base Certificate mismatch. Filename contains '${extractedScVer}' but expected base scNo '${scNo}'.`
        };
      }
      return { valid: true, slot: 'A', error: null };
    } else {
      const cleanDisplayScVersionNo = displayScVersionNo.toLowerCase();
      if (cleanExtractedScVer !== cleanDisplayScVersionNo) {
        return {
          valid: false,
          slot: 'B',
          error: `Versioned Certificate mismatch. Filename contains '${extractedScVer}' but expected '${displayScVersionNo}'.`
        };
      }
      return { valid: true, slot: 'B', error: null };
    }
  };

  const handleFilesSelect = (files: FileList | File[], targetSlot?: 'A' | 'B') => {
    const fileList = Array.from(files);
    if (fileList.length === 0) return;

    fileList.forEach(file => {
      if (file.type !== 'application/pdf') {
        notify(`File '${file.name}' is not a PDF. Only PDF files are allowed.`, 'error');
        return;
      }

      const result = validateFileName(file, targetSlot);
      if (result.slot === 'A') {
        if (result.valid) {
          setSelectedFileA(file);
          setErrorA(null);
          setSuccessA(true);
        } else {
          setSelectedFileA(null);
          setErrorA(result.error);
          setSuccessA(false);
          notify(`Validation failed for Slot A: ${result.error}`, 'error');
        }
      } else {
        if (hasNoAmendments) {
          notify(`Upload ignored: Amended slot is locked as no versioned amendments are available yet.`, 'error');
          return;
        }
        if (result.valid) {
          setSelectedFileB(file);
          setErrorB(null);
          setSuccessB(true);
        } else {
          setSelectedFileB(null);
          setErrorB(result.error);
          setSuccessB(false);
          notify(`Validation failed for Slot B: ${result.error}`, 'error');
        }
      }
    });
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelect(e.dataTransfer.files);
    }
  };

  const handleFileSubmit = async () => {
    if (!selectedFileA && !selectedFileB) {
      notify('Please select at least one file to upload', 'error');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      if (selectedFileA) {
        formData.append('files', selectedFileA);
      }
      if (selectedFileB) {
        formData.append('files', selectedFileB);
      }
      formData.append('sc_id', scId);
      formData.append('sc_no', scNo);
      formData.append('cb_id', cbLicensingCode);
      formData.append('status', submissionStatus);
      formData.append('sc_version_no', displayScVersionNo);
      // Append custom headers as Form data to bypass CORS preflight blocking:
      formData.append('te_cb_code', cbLicensingCode);
      formData.append('te_ui_source_key', 'TrackitPortalUI-DevKey');

      const result = await executePost(
        API_ROUTES.POST_SC_MULTIPART,
        formData
      );

      if (result?.status === 200) {
        notify('PDF(s) uploaded successfully', 'success');
        onUploadSuccess(result?.data);
        handleClose();
      } else {
        console.error('[PDF Upload] Non-200 response from server:', {
          status: result?.status,
          data: result?.data,
        });

        let errMsg = 'Error uploading PDF(s). Check browser console for details.';
        
        if (result?.data) {
          const data = result.data;
          
          // Extract general error message
          let backendMsg = data.message || (data.error && data.error.message) || null;
          
          // Parse nested downstream stringified JSON blocks
          if (backendMsg && backendMsg.includes("Downstream failure:")) {
            const jsonPart = backendMsg.replace("Downstream failure:", "").trim();
            try {
              const parsed = JSON.parse(jsonPart);
              if (parsed?.file_upload?.failed_files?.length > 0) {
                backendMsg = parsed.file_upload.failed_files[0].error;
              } else if (parsed?.message) {
                backendMsg = parsed.message;
              }
            } catch (e) {
              // Fail-safe: fallback to default if parsing fails
            }
          }
          
          // Check standard failed files array directly
          const failedFiles = data.file_upload?.failed_files || [];
          if (failedFiles.length > 0) {
            backendMsg = failedFiles[0].error || failedFiles[0].message || backendMsg;
          }
          
          if (backendMsg) {
            errMsg = backendMsg;
          }
        }

        notify(errMsg, 'error');
      }
    } catch (error: any) {
      console.error('[PDF Upload] Request exception:', {
        message: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
      });
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Error uploading PDF(s). Check browser console for details.';
      notify(errMsg, 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 12px 48px rgba(44, 71, 72, 0.12)',
          backgroundColor: '#FAFCFC'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: '#2C4748', pb: 2, px: 0, pt: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFileIcon sx={{ fontSize: 24, color: '#2C4748' }} /> Scope Certificate PDF Multi-Slot Uploader
      </DialogTitle>
      <DialogContent sx={{ px: 0, pb: 0 }}>
        {/* Unified Master Drag and Drop Box */}
        <Box
          onDragEnter={isUploadDisabled ? undefined : handleDrag}
          onDragOver={isUploadDisabled ? undefined : handleDrag}
          onDragLeave={isUploadDisabled ? undefined : handleDrag}
          onDrop={isUploadDisabled ? undefined : handleDrop}
          onClick={() => {
            if (uploading || isUploadDisabled) return;
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = '.pdf';
            input.onchange = (e: any) => {
              if (e.target.files) {
                handleFilesSelect(e.target.files);
              }
            };
            input.click();
          }}
          sx={{
            border: '2px dashed',
            borderColor: isUploadDisabled ? 'rgba(45, 106, 79, 0.3)' : (dragActive ? '#2C4748' : 'rgba(44, 71, 72, 0.3)'),
            borderRadius: 4,
            py: 5,
            px: 3,
            textAlign: 'center',
            cursor: (uploading || isUploadDisabled) ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.6 : 1,
            pointerEvents: (uploading || isUploadDisabled) ? 'none' : 'auto',
            backgroundColor: isUploadDisabled ? 'rgba(45, 106, 79, 0.02)' : (dragActive ? 'rgba(44, 71, 72, 0.08)' : '#FFFFFF'),
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.02)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            mb: 4,
            '&:hover': {
              borderColor: isUploadDisabled ? 'rgba(45, 106, 79, 0.3)' : '#2C4748',
              transform: (uploading || isUploadDisabled) ? 'none' : 'translateY(-1px)',
              boxShadow: (uploading || isUploadDisabled) ? '0 4px 16px rgba(0, 0, 0, 0.02)' : '0 8px 24px rgba(44, 71, 72, 0.08)',
              backgroundColor: (uploading || isUploadDisabled) ? 'rgba(45, 106, 79, 0.02)' : 'rgba(44, 71, 72, 0.01)'
            }
          }}
        >
          <UploadFileIcon sx={{ fontSize: 56, color: isUploadDisabled ? '#2d6a4f' : '#2C4748', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: isUploadDisabled ? '#2d6a4f' : '#2C4748', mb: 0.5 }}>
            {isBothAvailable 
              ? "Both PDF Certificates Already Uploaded" 
              : (isBaseAvailable && hasNoAmendments)
                ? "Original PDF Certificate Already Uploaded"
                : "Drag & Drop PDF file(s) here to Auto-Route"
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isBothAvailable 
              ? "No further uploads allowed as both Original and Amended PDFs are available." 
              : (isBaseAvailable && hasNoAmendments)
                ? "No further uploads allowed as the Original PDF is available and no versioned amendments exist."
                : "Or click to browse and select multiple files from your computer"
            }
          </Typography>
        </Box>

        {/* Grid of Slots */}
        <Grid container spacing={3}>
          {/* Slot A: Base Original */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 3.5,
                borderColor: isBaseAvailable ? '#2d6a4f' : (errorA ? '#d32f2f' : (successA ? '#2d6a4f' : 'rgba(44, 71, 72, 0.15)')),
                backgroundColor: '#FFFFFF',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.01)'
              }}
            >
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2C4748' }}>
                    Original Certificate (Base -00)
                  </Typography>
                  {isBaseAvailable ? (
                    <Chip label="Uploaded" color="success" size="small" sx={{ fontWeight: 'bold', backgroundColor: '#2d6a4f', color: '#FFFFFF' }} />
                  ) : successA ? (
                    <Chip label="Ready" color="success" size="small" sx={{ fontWeight: 'bold', backgroundColor: '#2d6a4f', color: '#FFFFFF' }} />
                  ) : (
                    <Chip label="Missing" color="default" size="small" variant="outlined" sx={{ borderStyle: 'dashed', color: 'rgba(44, 71, 72, 0.6)', borderColor: 'rgba(44, 71, 72, 0.4)' }} />
                  )}
                </Box>

                {/* Expectation Card Refactored Hierarchy */}
                {!isBaseAvailable && (
                  <Box sx={{ backgroundColor: 'rgba(240, 205, 127, 0.18)', p: 2, borderRadius: 3, border: '1.5px solid rgba(201, 151, 56, 0.35)', mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#8c6210', display: 'block', mb: 0.5 }}>
                      Suggested File Name Format
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontSize: '11px' }}>
                      Format: <strong>sc_[cb-code]_[sc-number]-00_[date].pdf</strong>
                    </Typography>
                    
                    {/* Hero Value Container Box */}
                    <Box 
                      sx={{ 
                        backgroundColor: '#FFFFFF', 
                        border: '1px solid rgba(201, 151, 56, 0.25)', 
                        borderRadius: '8px', 
                        p: 1.2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        gap: 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.01)'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: '#2C4748', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          flexGrow: 1,
                          fontSize: '11.5px'
                        }}
                      >
                        {templateNameA}
                      </Typography>
                      <Button 
                        size="small" 
                        variant="text" 
                        onClick={copyTemplateA} 
                        sx={{ minWidth: 0, p: 0.5, textTransform: 'none', color: '#8c6210', fontWeight: 'bold', fontSize: '12px', textDecoration: 'underline' }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Status Box */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 3, 
                  border: '1.5px dashed',
                  borderColor: isBaseAvailable ? '#2d6a4f' : (errorA ? '#d32f2f' : (successA ? '#2d6a4f' : 'rgba(44, 71, 72, 0.2)')),
                  backgroundColor: isBaseAvailable ? 'rgba(45, 106, 79, 0.02)' : (errorA ? 'rgba(211, 47, 47, 0.02)' : (successA ? 'rgba(45, 106, 79, 0.02)' : 'transparent')),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '120px',
                  textAlign: 'center'
                }}
              >
                {selectedFileA ? (
                  uploading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
                      <CircularProgress size={32} sx={{ color: '#2C4748', mb: 1.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', mb: 0.5 }}>
                        Uploading base certificate...
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Please do not close this window
                      </Typography>
                    </Box>
                  ) : (
                    <Box width="100%">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', wordBreak: 'break-all', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <UploadFileIcon sx={{ fontSize: 18, color: '#2C4748' }} /> {selectedFileA.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {(selectedFileA.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small" 
                        onClick={() => {
                          setSelectedFileA(null);
                          setSuccessA(false);
                          setErrorA(null);
                        }}
                        sx={{ borderRadius: '12px', textTransform: 'none', px: 2.5, py: 0.3, fontWeight: 'bold' }}
                      >
                        Remove File
                      </Button>
                    </Box>
                  )
                ) : (
                  isBaseAvailable ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" sx={{ py: 1 }}>
                      <Alert severity="success" sx={{ mb: 1, borderRadius: '8px', fontSize: '11.5px', textAlign: 'left', width: '100%', py: 0.5, backgroundColor: 'rgba(45, 106, 79, 0.05)', color: '#2d6a4f' }}>
                        Original Base PDF is already available.
                      </Alert>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>
                        You only need to upload the amended certificate in Slot B if applicable.
                      </Typography>
                    </Box>
                  ) : (
                    <Box width="100%">
                      {errorA ? (
                        <Box width="100%">
                          <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px', fontSize: '12px', textAlign: 'left' }}>
                            {errorA}
                          </Alert>
                          <Button
                            size="small"
                            variant="text"
                            disabled={uploading}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.pdf';
                              input.onchange = (e: any) => {
                                if (e.target.files) handleFilesSelect(e.target.files, 'A');
                              };
                              input.click();
                            }}
                            sx={{ textTransform: 'none', color: '#2C4748', fontWeight: 600, textDecoration: 'underline' }}
                          >
                            Choose matching file
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '13px' }}>
                            No file selected for Slot A
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            disabled={uploading}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.pdf';
                              input.onchange = (e: any) => {
                                if (e.target.files) handleFilesSelect(e.target.files, 'A');
                              };
                              input.click();
                            }}
                            sx={{ textTransform: 'none', color: '#2C4748', fontWeight: 600, p: 0, textDecoration: 'underline' }}
                          >
                            Browse file directly
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Slot B: Active Versioned */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 3.5,
                borderColor: hasNoAmendments ? 'rgba(44, 71, 72, 0.1)' : (isVersionAvailable ? '#2d6a4f' : (errorB ? '#d32f2f' : (successB ? '#2d6a4f' : 'rgba(44, 71, 72, 0.15)'))),
                backgroundColor: hasNoAmendments ? '#F4F7F7' : '#FFFFFF',
                opacity: hasNoAmendments ? 0.75 : 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.01)'
              }}
            >
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: hasNoAmendments ? 'rgba(44, 71, 72, 0.5)' : '#2C4748' }}>
                    Amended Certificate (Versioned {versionSuffix})
                  </Typography>
                  {hasNoAmendments ? (
                    <Chip label="Locked" size="small" variant="outlined" sx={{ borderStyle: 'dashed', color: 'rgba(44, 71, 72, 0.4)', borderColor: 'rgba(44, 71, 72, 0.3)' }} />
                  ) : isVersionAvailable ? (
                    <Chip label="Uploaded" color="success" size="small" sx={{ fontWeight: 'bold', backgroundColor: '#2d6a4f', color: '#FFFFFF' }} />
                  ) : successB ? (
                    <Chip label="Ready" color="success" size="small" sx={{ fontWeight: 'bold', backgroundColor: '#2d6a4f', color: '#FFFFFF' }} />
                  ) : (
                    <Chip label="Missing" color="default" size="small" variant="outlined" sx={{ borderStyle: 'dashed', color: 'rgba(44, 71, 72, 0.6)', borderColor: 'rgba(44, 71, 72, 0.4)' }} />
                  )}
                </Box>

                {/* Expectation Card Refactored Hierarchy */}
                {!isVersionAvailable && !hasNoAmendments && (
                  <Box sx={{ backgroundColor: 'rgba(240, 205, 127, 0.18)', p: 2, borderRadius: 3, border: '1.5px solid rgba(201, 151, 56, 0.35)', mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#8c6210', display: 'block', mb: 0.5 }}>
                      Suggested File Name Format
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontSize: '11px' }}>
                      Format: <strong>sc_[cb-code]_[sc-number]-[version]_[date].pdf</strong>
                    </Typography>
                    
                    {/* Hero Value Container Box */}
                    <Box 
                      sx={{ 
                        backgroundColor: '#FFFFFF', 
                        border: '1px solid rgba(201, 151, 56, 0.25)', 
                        borderRadius: '8px', 
                        p: 1.2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        gap: 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.01)'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: '#2C4748', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          flexGrow: 1,
                          fontSize: '11.5px'
                        }}
                      >
                        {templateNameB}
                      </Typography>
                      <Button 
                        size="small" 
                        variant="text" 
                        onClick={copyTemplateB} 
                        sx={{ minWidth: 0, p: 0.5, textTransform: 'none', color: '#8c6210', fontWeight: 'bold', fontSize: '12px', textDecoration: 'underline' }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Status Box */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 3, 
                  border: hasNoAmendments ? '1.5px dashed rgba(44, 71, 72, 0.1)' : ('1.5px dashed ' + (errorB ? '#d32f2f' : (successB ? '#2d6a4f' : 'rgba(44, 71, 72, 0.2)'))),
                  backgroundColor: hasNoAmendments ? 'rgba(44, 71, 72, 0.02)' : (errorB ? 'rgba(211, 47, 47, 0.02)' : (successB ? 'rgba(45, 106, 79, 0.02)' : 'transparent')),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '120px',
                  textAlign: 'center'
                }}
              >
                {hasNoAmendments ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" sx={{ py: 1 }}>
                    <Alert severity="info" sx={{ mb: 1, borderRadius: '8px', fontSize: '11.5px', textAlign: 'left', width: '100%', py: 0.5, backgroundColor: 'rgba(44, 71, 72, 0.05)', color: '#2C4748', '& .MuiAlert-icon': { color: '#2C4748' } }}>
                      No amended version is available yet.
                    </Alert>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>
                      This Scope Certificate does not contain any versioned amendments in the system.
                    </Typography>
                  </Box>
                ) : selectedFileB ? (
                  uploading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
                      <CircularProgress size={32} sx={{ color: '#2C4748', mb: 1.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', mb: 0.5 }}>
                        Uploading amended certificate...
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Please do not close this window
                      </Typography>
                    </Box>
                  ) : (
                    <Box width="100%">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C4748', wordBreak: 'break-all', mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <UploadFileIcon sx={{ fontSize: 18, color: '#2C4748' }} /> {selectedFileB.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {(selectedFileB.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small" 
                        onClick={() => {
                          setSelectedFileB(null);
                          setSuccessB(false);
                          setErrorB(null);
                        }}
                        sx={{ borderRadius: '12px', textTransform: 'none', px: 2.5, py: 0.3, fontWeight: 'bold' }}
                      >
                        Remove File
                      </Button>
                    </Box>
                  )
                ) : (
                  isVersionAvailable ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" sx={{ py: 1 }}>
                      <Alert severity="success" sx={{ mb: 1, borderRadius: '8px', fontSize: '11.5px', textAlign: 'left', width: '100%', py: 0.5, backgroundColor: 'rgba(45, 106, 79, 0.05)', color: '#2d6a4f' }}>
                        Amended Certificate PDF is already available.
                      </Alert>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>
                        No further action required.
                      </Typography>
                    </Box>
                  ) : (
                    <Box width="100%">
                      {errorB ? (
                        <Box width="100%">
                          <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px', fontSize: '12px', textAlign: 'left' }}>
                            {errorB}
                          </Alert>
                          <Button
                            size="small"
                            variant="text"
                            disabled={uploading}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.pdf';
                              input.onchange = (e: any) => {
                                if (e.target.files) handleFilesSelect(e.target.files, 'B');
                              };
                              input.click();
                            }}
                            sx={{ textTransform: 'none', color: '#2C4748', fontWeight: 600, textDecoration: 'underline' }}
                          >
                            Choose matching file
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '13px' }}>
                            No file selected for Slot B
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            disabled={uploading}
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = '.pdf';
                              input.onchange = (e: any) => {
                                if (e.target.files) handleFilesSelect(e.target.files, 'B');
                              };
                              input.click();
                            }}
                            sx={{ textTransform: 'none', color: '#2C4748', fontWeight: 600, p: 0, textDecoration: 'underline' }}
                          >
                            Browse file directly
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 0, pb: 0, pt: 3 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          sx={{
            height: "40px",
            textTransform: 'none',
            color: '#2C4748',
            fontWeight: 600,
            px: 3
          }}
        >
          Cancel
        </Button>
        <StyledButton
          onClick={handleFileSubmit}
          disabled={(!selectedFileA && !selectedFileB) || uploading || isUploadDisabled}
          variant="contained"
          sx={{
            height: "40px",
            backgroundColor: '#F0CD7F',
            color: '#1C1C1C',
            '&:hover': {
              backgroundColor: '#E0B86F'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(240, 205, 127, 0.4)',
              color: 'rgba(28, 28, 28, 0.4)'
            }
          }}
        >
          {uploading ? <CircularProgress size={20} color="inherit" /> : "Upload Files"}
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};
